import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDomains } from './useHomePageData';
import { usePermissions } from './usePermissions';
import { type Domain } from '../services/api';

export type SendingTypeScope = 'transactional-only' | 'marketing-only' | 'mixed' | 'none' | 'unknown';
export const sendingTypeScopeKey = (workspaceId: string) => ['sendingTypeScope', workspaceId] as const;

export function sendingTypesForScope(scope: SendingTypeScope): string[] {
  if (scope === 'none') return [];
  if (scope === 'transactional-only') return ['transactional'];
  if (scope === 'marketing-only') return ['marketing'];
  return ['marketing', 'transactional'];
}

export function useSendingTypeScope() {
  const { workspaceId, hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const canReadDomains = hasPermission('domain:read');
  const domains = useDomains(canReadDomains);
  const scope = useMemo<SendingTypeScope>(() => {
    if (!canReadDomains || !domains.isSuccess) return 'unknown';
    const verified = (domains.data as Domain[]).filter(domain => domain.status === 'SUCCESS');
    if (!verified.length) return 'none';
    const types = new Set(verified.map(domain => domain.useCase === 'mk' ? 'marketing' : 'transactional'));
    return types.size > 1 ? 'mixed' : types.has('marketing') ? 'marketing-only' : 'transactional-only';
  }, [canReadDomains, domains.data, domains.isSuccess]);

  useQuery({
    queryKey: sendingTypeScopeKey(workspaceId),
    queryFn: () => scope,
    initialData: scope,
    enabled: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (workspaceId) queryClient.setQueryData(sendingTypeScopeKey(workspaceId), scope);
  }, [queryClient, workspaceId, scope]);

  return {
    sendingTypeScope: scope,
    sendingTypes: sendingTypesForScope(scope),
    isReady: !canReadDomains || domains.isSuccess || domains.isError,
    workspaceId,
  };
}
